import React, { useState } from "react";
import api, { formatApiError } from "@/api";
import { X } from "lucide-react";

export default function AddHoldingModal({ open, onClose, onAdded, initialTicker = "", initialCost = "" }) {
  const [ticker, setTicker] = useState("");
  const [shares, setShares] = useState("");
  const [costBasis, setCostBasis] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  React.useEffect(() => {
    if (open) {
      setTicker(initialTicker || "");
      setCostBasis(initialCost ? String(initialCost) : "");
      setShares("");
      setError("");
      setPreview(null);
    }
  }, [open, initialTicker, initialCost]);

  if (!open) return null;

  const reset = () => {
    setTicker(""); setShares(""); setCostBasis("");
    setError(""); setPreview(null); setBusy(false);
  };

  const onPreview = async () => {
    if (!ticker) return;
    setBusy(true); setError(""); setPreview(null);
    try {
      const { data } = await api.get(`/market/quote/${ticker.toUpperCase()}`);
      setPreview(data);
      if (data.quote?.price && !costBasis) {
        setCostBasis(String(data.quote.price));
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/holdings", {
        ticker: ticker.toUpperCase(),
        shares: parseFloat(shares),
        cost_basis: parseFloat(costBasis),
      });
      onAdded();
      reset();
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="add-holding-modal-backdrop"
    >
      <div
        className="card w-full max-w-md fade-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="add-holding-modal"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="label-xs mb-1">New position</p>
            <h3 className="font-serif text-2xl">Add holding</h3>
          </div>
          <button className="btn-ghost" onClick={() => { reset(); onClose(); }} data-testid="close-modal-button">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="label-xs block mb-2">Ticker symbol</label>
            <div className="flex gap-2">
              <input
                data-testid="add-holding-ticker-input"
                className="input-base uppercase" placeholder="e.g. AAPL"
                value={ticker}
                onChange={(e) => setTicker(e.target.value.toUpperCase())}
                required maxLength={10}
              />
              <button
                data-testid="add-holding-preview-button"
                type="button" className="btn-secondary whitespace-nowrap"
                onClick={onPreview} disabled={!ticker || busy}
              >
                Lookup
              </button>
            </div>
          </div>

          {preview && (
            <div className="border p-3 text-xs space-y-1" style={{ borderColor: "var(--border)" }} data-testid="add-holding-preview">
              <div className="flex justify-between">
                <span style={{ color: "var(--text-secondary)" }}>Name</span>
                <span>{preview.overview?.name || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: "var(--text-secondary)" }}>Price</span>
                <span>{preview.quote?.price ? `$${preview.quote.price.toFixed(2)}` : "—"}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: "var(--text-secondary)" }}>Div / share (annual)</span>
                <span>{preview.overview?.dividend_per_share != null ? `$${preview.overview.dividend_per_share.toFixed(2)}` : "—"}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: "var(--text-secondary)" }}>Yield</span>
                <span style={{ color: "var(--gain)" }}>
                  {preview.overview?.dividend_yield != null ? `${(preview.overview.dividend_yield * 100).toFixed(2)}%` : "—"}
                </span>
              </div>
              {(preview.quote?.error || preview.overview?.error) && (
                <div className="text-[10px] mt-2" style={{ color: "var(--loss)" }}>
                  {preview.quote?.error || preview.overview?.error}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-xs block mb-2">Shares</label>
              <input
                data-testid="add-holding-shares-input"
                className="input-base" type="number" step="any" min="0.0001"
                value={shares} onChange={(e) => setShares(e.target.value)} required
              />
            </div>
            <div>
              <label className="label-xs block mb-2">Cost / share ($)</label>
              <input
                data-testid="add-holding-cost-input"
                className="input-base" type="number" step="any" min="0"
                value={costBasis} onChange={(e) => setCostBasis(e.target.value)} required
              />
            </div>
          </div>

          {error && (
            <div data-testid="add-holding-error" className="text-xs px-3 py-2"
              style={{ background: "var(--loss-bg)", color: "var(--loss)", border: "1px solid var(--loss)" }}>
              {error}
            </div>
          )}

          <button data-testid="add-holding-submit-button" type="submit" disabled={busy} className="btn-primary w-full">
            {busy ? "Saving..." : "Add position →"}
          </button>
        </form>
      </div>
    </div>
  );
}
